import { Description, InfoOutlined } from '@mui/icons-material';
import { Button, Divider, lighten, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import ChipLabel from '/@/components/chip-label';
import { getFieldMonitoringLabelStep } from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS';
import FieldCard from '/@/components/field-info-v2/field-card';
import FieldSkeleton from '/@/components/field-skeleton';
import FieldsEmpty from '/@/components/fields-empty';
import Icon from '/@/components/icon';
import PlantingReportInfoCard from '/@/components/planting-report-info-card';
import useUnitProvider from '/@/core/context/unit';
import useDebounce from '/@/hooks/debounce/useDebounce';
import type { useMyFieldsInfinite } from '/@/hooks/fields';
import { useGetUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import { reverse } from '/@/utils/router';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import CreateReportModal from '/@/views/fields/field-monitoring/components/create-report-modal';
import CropMonitoringDialog from '/@/views/fields/field-monitoring/components/creation-dialog';
import type { UnitProviderProps } from '/@/views/visit-reports/edit/components/field-observation-section/components/precipitation-graphic';
import type { FieldListV2FormType } from '../..';
import InfiniteLoader from './components/infinite-loader';
import FieldListV2MoreOptions from './components/more-options';
import WeatherForecastMiniCard from './components/weather-mini-card';
import { type PlantingMergedResult, usePlantingReportResultBulk } from './hooks';

export const FieldListV2SidebarField = ({
    field,
    isSelected,
    selectedFieldRef,
    form,
    isRVView,
    headerText,
    collapsedVersion = false,
    userNodeQuery,
    unitProviderProps,
    canEditResource,
    chips,
    recentPlantingResult,
}: {
    field: Field;
    isSelected: boolean;
    selectedFieldRef: React.RefObject<HTMLDivElement>;
    form?: UseFormReturn<FieldListV2FormType>;
    isRVView: boolean;
    headerText?: string;
    collapsedVersion?: boolean;
    userNodeQuery: ReturnType<typeof useGetUserNode>;
    unitProviderProps?: UnitProviderProps;
    canEditResource: boolean;
    chips?: (typeof ChipLabel)[];
    recentPlantingResult?: PlantingMergedResult | null;
}) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const units = useUnitProvider(unitProviderProps);

    const cropData = getCropMonitoringData(field.activeCropMonitoring);
    const crops = getCrops();
    // Create Report Modal
    const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [newCMModalOpened, setNewCMModalOpened] = useState(false);

    const defaultChips = useMemo(() => {
        const cropName = crops[field.activeCropMonitoring?.crop || 'fallow']?.name;
        const cropIcon = crops[field.activeCropMonitoring?.crop || 'fallow']?.icon;
        const availableChips = [
            {
                label: `${_('Campaña')} ${field?.campaign}`,
                type: 'campaign',
                enabled: !!field?.campaign,
            },
            {
                label: `${cropName} ${field.activeCropMonitoring?.productName}`,
                type: field.activeCropMonitoring?.crop,
                icon: cropIcon,
                enabled: !!field.activeCropMonitoring?.crop,
            },
            {
                label: field.activeCropMonitoring?.cornCropMonitoring
                    ? getFieldMonitoringLabelStep(
                          field.activeCropMonitoring?.cornCropMonitoring?.vegetativeStage || undefined,
                          field.activeCropMonitoring?.cornCropMonitoring?.phenology || undefined,
                          field.activeCropMonitoring?.plantingDate
                      )
                    : _('Barbecho'),
                type: 'fallow',
                enabled: !field.activeCropMonitoring || field.activeCropMonitoring?.cornCropMonitoring?.id,
            },
        ];
        return availableChips.filter((chip) => chip.enabled);
    }, [field.activeCropMonitoring]);

    return (
        <Stack
            key={field.id}
            ref={isSelected ? selectedFieldRef : null}
            justifyContent='space-between'
            p={2}
            spacing={1.5}
            data-testid='field-list-v2-sidebar-field'
            width='100%'
            sx={[
                {
                    position: 'relative',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: lighten('#F5F5F5', 0.75),
                    },
                },
                isSelected && {
                    backgroundColor: '#F5F5F5',
                    '&:hover': { backgroundColor: '#F5F5F5' },
                },
            ]}
            onClick={() => {
                mpTrackEvent('Field detail opened', {
                    entry_source: 'list',
                });
                form?.setValue('fieldId', field.id);
            }}
        >
            <Stack className='mp-mask' direction='row' justifyContent='space-between' alignItems='start'>
                <Stack spacing={1.5} pr='40px'>
                    {isRVView && (
                        <Typography variant='overline' color='primary.mui.inactive'>
                            {headerText ?? (userNodeQuery.data?.isSelf ? _('Mi cuenta') : userNodeQuery.data?.name)}
                        </Typography>
                    )}
                    <FieldCard
                        name={field.name}
                        geometry={field.geometry}
                        area={field.area}
                        location={field.location}
                        selected={isSelected}
                        unitProviderProps={unitProviderProps}
                        isFieldview={field.externalProvider === 'fieldview'}
                    />
                </Stack>
                {!collapsedVersion && <FieldListV2MoreOptions field={field} form={form} />}
            </Stack>
            <Stack direction='row' spacing={0.5} useFlexGap flexWrap='wrap'>
                {(chips ?? defaultChips).map((chip) => (
                    <ChipLabel key={chip.label} label={chip.label} type={chip.type} icon={chip.icon} />
                ))}
            </Stack>
            {!isSelected && recentPlantingResult && (
                <Stack
                    p={1.5}
                    sx={{
                        backgroundColor: '#E7F8FF',
                    }}
                >
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <InfoOutlined sx={{ fontSize: 18 }} color='primary' />
                        <Typography variant='subtitle2' fontWeight='bold' color='primary'>
                            {_('Se detectaron datos de siembra para este lote')}
                        </Typography>
                    </Stack>
                </Stack>
            )}
            {isSelected && !collapsedVersion && (
                <>
                    {field.activeCropMonitoring?.plantingDate && (
                        <Stack direction='row' spacing={0.5}>
                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                {`${_('Fecha de siembra')}: `}
                            </Typography>
                            <Typography variant='subtitle2' color='primary'>
                                {formatDate(new Date(field.activeCropMonitoring.plantingDate))}
                            </Typography>
                        </Stack>
                    )}
                    {field.activeCropMonitoring && createReportModalIsOpen && (
                        <CreateReportModal
                            field={field}
                            cropMonitoring={field.activeCropMonitoring as CropMonitoring}
                            isOpen={createReportModalIsOpen}
                            setIsOpen={setCreateReportModalIsOpen}
                            type='fieldview'
                            setShowErrorModal={setShowErrorModal}
                            showErrorModal={showErrorModal}
                        />
                    )}
                    {cropData?.expectedHarvestDate && (
                        <Stack direction='row' spacing={0.5}>
                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                {`${_('Fecha de cosecha')}: `}
                            </Typography>
                            <Typography variant='subtitle2' color='primary'>
                                {formatDate(new Date(cropData?.expectedHarvestDate))}
                            </Typography>
                        </Stack>
                    )}
                    {field.activeCropMonitoring?.result?.averageYield && (
                        <Stack direction='row' spacing={0.5}>
                            <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                                {`${_('Rendimiento')}: `}
                            </Typography>
                            <Typography variant='subtitle2' color='primary'>
                                {units.toYield(field.activeCropMonitoring.result.averageYield, 'corn', 2).toString()}
                            </Typography>
                        </Stack>
                    )}
                    <WeatherForecastMiniCard lat={field.lat} lng={field.lng} />
                    {recentPlantingResult && (
                        <PlantingReportInfoCard
                            recentPlantingResult={recentPlantingResult}
                            actions={
                                <>
                                    <Button
                                        variant='text'
                                        startIcon={
                                            <Icon
                                                type='agronomy'
                                                size='xs'
                                                sx={{
                                                    fill: (theme.palette.primary as { onSurface: string }).onSurface,
                                                }}
                                            />
                                        }
                                        sx={{ mr: 'auto' }}
                                        onClick={() => setNewCMModalOpened(true)}
                                    >
                                        {_('Agregar próximo cultivo')}
                                    </Button>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        sx={{ ml: 'auto' }}
                                        onClick={() =>
                                            navigate(reverse('fields.FieldDetail', { params: { fieldId: field.id } }))
                                        }
                                    >
                                        {_('Ir al detalle del lote')}
                                    </Button>
                                </>
                            }
                        />
                    )}
                    <Stack direction='row' width='100%' alignItems='center'>
                        {!field.activeCropMonitoring?.result &&
                            field.activeCropMonitoring?.cornCropMonitoring?.currentStage === 'harvested' && (
                                <Button
                                    variant='text'
                                    startIcon={
                                        <Description
                                            sx={{
                                                color: theme.palette.primary.onSurface,
                                            }}
                                        />
                                    }
                                    sx={{
                                        mr: 'auto',
                                    }}
                                    onClick={() => {
                                        setCreateReportModalIsOpen(true);
                                    }}
                                >
                                    {_('Cargar datos de cosecha')}
                                </Button>
                            )}
                        {field.activeCropMonitoring?.result && (
                            <Button
                                variant='text'
                                startIcon={
                                    <Description
                                        sx={{
                                            color: (t: any) => t.palette.primary.onSurface,
                                        }}
                                    />
                                }
                                sx={{
                                    mr: 'auto',
                                }}
                                onClick={() => {
                                    navigate(
                                        reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                            params: {
                                                cropMonitoringId: field.activeCropMonitoring?.id,
                                            },
                                        })
                                    );
                                }}
                            >
                                {_('Ver reporte de cosecha')}
                            </Button>
                        )}
                        {!recentPlantingResult && !field.activeCropMonitoring && canEditResource && (
                            <Button
                                variant='text'
                                data-testid='field-list-v2-sidebar-field-add-crop-monitoring'
                                startIcon={
                                    <Icon
                                        type='agronomy'
                                        size='xs'
                                        sx={{ fill: (theme.palette.primary as { onSurface: string }).onSurface }}
                                    />
                                }
                                sx={{
                                    mr: 'auto',
                                }}
                                onClick={() => {
                                    setNewCMModalOpened(true);
                                }}
                            >
                                {_('Agregar próximo cultivo')}
                            </Button>
                        )}
                        {!recentPlantingResult && (
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{
                                    ml: 'auto',
                                }}
                                onClick={() => {
                                    navigate(reverse('fields.FieldDetail', { params: { fieldId: field.id } }));
                                }}
                            >
                                {_('Ir al detalle del lote')}
                            </Button>
                        )}
                    </Stack>
                </>
            )}
            <CropMonitoringDialog
                open={newCMModalOpened}
                setClose={() => setNewCMModalOpened(false)}
                field={field}
                entry_source='field-list'
                plantingReport={recentPlantingResult ?? undefined}
            />
        </Stack>
    );
};

const FieldWrapper = (
    props: Omit<React.ComponentProps<typeof FieldListV2SidebarField>, 'userNodeQuery' | 'canEditResource'>
) => {
    const userNodeQuery = useGetUserNode(props.field.userNode);
    const canEditResource = useHasWriteAccess();

    return <FieldListV2SidebarField {...props} userNodeQuery={userNodeQuery} canEditResource={canEditResource} />;
};

const FieldListV2Sidebar = ({
    form,
    fieldsQuery,
    isRVView,
    userNodeId,
    emptyMessage,
}: {
    form: UseFormReturn<FieldListV2FormType>;
    fieldsQuery: ReturnType<typeof useMyFieldsInfinite>;
    isRVView: boolean;
    userNodeId: number;
    emptyMessage: string;
}) => {
    const selectedFieldRef = useRef<HTMLDivElement>(null);

    const selectedFieldId = form.watch('fieldId');
    const field = fieldsQuery.data
        ?.flatMap((page: any) => page.results)
        .find((field: any) => field.id === selectedFieldId);

    const fields = useMemo(() => {
        if (!fieldsQuery.data) return [];

        const fields = fieldsQuery.data.flatMap((page: any) => page.results);
        if (field && form.getValues('fieldId') && !fields.find((f: any) => f.id === field.id)) {
            fields.unshift(field);
        }

        return fields;
    }, [fieldsQuery.data, field]);

    // Bulk-fetch planting reports for all visible fields — 1 request instead of N
    const plantingResultsByField = usePlantingReportResultBulk(fields);

    useEffect(() => {
        if (field && selectedFieldRef.current) {
            selectedFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [field]);

    const debouncedFieldName = useDebounce(form.watch('name'), 500);
    const fieldValues = form.watch(['producerId', 'stage', 'plantingDateFrom', 'plantingDateTo', 'crop']);
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);

    return (
        <Stack width='100%'>
            {fields?.length === 0 &&
                (!debouncedFieldName && !fieldValues.some(Boolean) ? (
                    <>
                        <Stack divider={<Divider flexItem />}>
                            {[...Array(fieldsQuery.isValidating ? 8 : 3)].map((_, i) => (
                                <FieldSkeleton key={i} skeletonProps={{ animation: false }} />
                            ))}
                        </Stack>
                        {!fieldsQuery.isValidating && (
                            <FieldsEmpty
                                onClick={() => {
                                    setNewFieldModalOpen(true);
                                }}
                                onAddFields={() => {
                                    setNewFieldModalOpen(true);
                                }}
                                emptyMessage={emptyMessage}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <Stack divider={<Divider flexItem />}>
                            {[...Array(fieldsQuery.isValidating ? 8 : 3)].map((_, i) => (
                                <FieldSkeleton key={i} skeletonProps={{ animation: false }} />
                            ))}
                        </Stack>
                        <FieldsEmpty filtering emptyMessage={emptyMessage} />
                    </>
                ))}
            {fields?.length > 0 && (
                <>
                    {fields.map((field: any, index: number) => {
                        const isSelected = field.id === selectedFieldId;
                        return (
                            <FieldWrapper
                                key={field.id}
                                field={field}
                                isSelected={isSelected}
                                selectedFieldRef={selectedFieldRef}
                                form={form}
                                recentPlantingResult={plantingResultsByField.get(field.id) ?? null}
                                isRVView={isRVView}
                            />
                        );
                    })}

                    <InfiniteLoader infiniteQuery={fieldsQuery} pageSize={10} />
                </>
            )}
            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                rvUserNodeId={userNodeId}
                growerUserNodeId={isRVView ? undefined : userNodeId}
                trackEntrySource='field-list'
            />
        </Stack>
    );
};

export default FieldListV2Sidebar;
